import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../store/alert/alertSlice';
import { getTeamMembersForApproval, sendBookingApproval, sendCatalogApproval, sendTourApproval } from '../utils/approvalUtils';
import { CustomCalendar } from './Calendar';

const ApprovalModal = ({ isOpen, onClose, itemData, itemType, onApprovalSent }) => {
  const dispatch = useDispatch();
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTeamMembers();
    }
  }, [isOpen]);

  const fetchTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      const members = await getTeamMembersForApproval();
      setTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team members:', error);
      dispatch(setErrorAlert('Failed to load team members'));
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedApprover) {
      dispatch(setErrorAlert('Please select an approver'));
      return;
    }

    setLoading(true);
    try {
      const selectedMember = teamMembers.find(member => member._id === selectedApprover);
      const approverName = selectedMember?.name || selectedMember?.username || 'Team Member';

      // Add additional data to item
      const enhancedItemData = {
        ...itemData,
        priority,
        approvalDeadline: dueDate || null,
        approvalComments: comments
      };

      let result;
      switch (itemType) {
        case 'booking':
          result = await sendBookingApproval(enhancedItemData, selectedApprover, approverName);
          break;
        case 'catalog':
          result = await sendCatalogApproval(enhancedItemData, selectedApprover, approverName);
          break;
        case 'tour':
        case 'flight':
        case 'hotel':
        case 'transport':
          result = await sendTourApproval(enhancedItemData, selectedApprover, approverName, itemType);
          break;
        default:
          throw new Error('Invalid item type');
      }

      if (result.success) {
        dispatch(setSuccessAlert(`Approval request sent to ${approverName}`));
        onApprovalSent && onApprovalSent(result.data);
        onClose();
        resetForm();
      } else {
        dispatch(setErrorAlert(result.error || 'Failed to send approval request'));
      }
    } catch (error) {
      console.error('Error sending approval:', error);
      dispatch(setErrorAlert('Failed to send approval request'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedApprover('');
    setPriority('medium');
    setDueDate('');
    setComments('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const getItemTitle = () => {
    switch (itemType) {
      case 'booking':
        return itemData?.venue || itemData?.title || 'Booking';
      case 'catalog':
        return itemData?.title || itemData?.songTitle || 'Catalog Item';
      case 'flight':
        return `Flight: ${itemData?.departure || 'TBD'} to ${itemData?.arrival || 'TBD'}`;
      case 'hotel':
        return itemData?.name || itemData?.hotelName || 'Hotel Booking';
      case 'transport':
        return `Transport: ${itemData?.type || 'Ground Transport'}`;
      default:
        return itemData?.title || 'Item';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-6 w-full max-w-[400px] mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-[14px] font-medium">Submit</h2>
          <span
            onClick={handleClose}
            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
          >
            Close
          </span>
        </div>

        <div className="mb-6">
          <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.4]">
            Send <span className="text-white font-medium">{getItemTitle()}</span> for approval
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Approver Selection */}
          <div>
            <label className="block text-white text-[12px] font-medium mb-2">
              Send to *
            </label>
            {loadingTeam ? (
              <div className="text-[rgba(255,255,255,0.5)] text-[12px]">Loading team members...</div>
            ) : (
              <select
                value={selectedApprover}
                onChange={(e) => setSelectedApprover(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white rounded-[8px] px-3 py-2 pr-8 text-[12px] focus:outline-none focus:border-[#FF3B30] transition-colors cursor-pointer"
                style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                required
              >
                <option value="" className="bg-black">Select approver...</option>
                {teamMembers.map((member) => (
                  <option key={member._id} value={member._id} className="bg-black">
                    {member.name || member.username} ({member.role || member.professionRole})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-white text-[12px] font-medium mb-2">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white rounded-[8px] px-3 py-2 pr-8 text-[12px] focus:outline-none focus:border-[#FF3B30] transition-colors cursor-pointer"
              style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
            >
              <option value="low" className="bg-black">Low</option>
              <option value="medium" className="bg-black">Medium</option>
              <option value="high" className="bg-black">High</option>
              <option value="urgent" className="bg-black">Urgent</option>
            </select>
          </div>

          {/* Due Date */}
          <CustomCalendar
            label="Approval Deadline (Optional)"
            value={dueDate}
            onChange={(date) => setDueDate(date)}
            placeholder="Select deadline..."
            showTime={true}
            theme="workflows"
          />

          {/* Comments */}
          <div>
            <label className="block text-white text-[12px] font-medium mb-2">
              Additional Comments (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white rounded-[8px] px-3 py-2 text-[12px] focus:outline-none focus:border-[#FF3B30] transition-colors resize-none"
              placeholder="Add any additional context or requirements..."
              rows="3"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-[#121212] hover:bg-[#1a1a1a] text-white text-[12px] font-medium py-2 px-4 rounded-[8px] transition-colors focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedApprover || loadingTeam}
              className="bg-white hover:bg-gray-100 text-black text-[12px] font-medium py-2 px-4 rounded-[8px] transition-all focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[rgba(255,255,255,0.25)] disabled:text-white"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovalModal;
